/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import OrganizationGroupTypeForm from '../components/applications/OrganizationGroupTypeForm';
import ProgressBarDialog from '../components/common/ProgressBar';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

export default function OrganizationGroupTypesPage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();

    const [loading, setLoading] = useState(true);
    const [groupTypes, setGroupTypes] = useState([]);
    const [hierarchies, setHierarchies] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Load Data
    useEffect(() => {
        fetchData();
    }, [applicationId, refreshKey]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Parallel fetch for types and hierarchies
            const [typesResp, hierarchyResp] = await Promise.all([
                apiHelper.apiCall(`/arcee/group-types/?application_id=${applicationId}`),
                apiHelper.apiCall(`/arcee/group-type-hierarchies/?application_id=${applicationId}`)
            ]);

            if (typesResp.ok && hierarchyResp.ok) {
                const types = await typesResp.json();
                const hier = await hierarchyResp.json();
                setGroupTypes(Array.isArray(types) ? types : (types.results || []));
                setHierarchies(Array.isArray(hier) ? hier : (hier.results || []));
            } else {
                throw new Error("Failed to load data");
            }
        } catch (error) {
            console.error(error);
            showAlertDialog({
                title: 'Error',
                message: 'Failed to load group types configuration',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteType = (type) => {
        showAlertDialog({
            title: 'Delete Group Type',
            message: `Are you sure you want to delete "${type.name}"? This may fail if it is in use.`,
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/group-types/${type.id}/`, {
                        method: 'DELETE'
                    });
                    if (!resp.ok) {
                        const err = await resp.json();
                        throw new Error(err.error || 'Failed to delete');
                    }
                    setRefreshKey(k => k + 1);
                } catch (error) {
                    showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
                }
            }
        });
    };

    const handleAddHierarchy = async (parentType, childType) => {
        if (!parentType || !childType || parentType.id === childType.id) return;

        try {
            const resp = await apiHelper.apiCall('/arcee/group-type-hierarchies/create/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    parent_type: parentType.id,
                    child_type: childType.id
                })
            });

            if (!resp.ok) {
                const err = await resp.json();
                throw new Error(err.error || 'Failed to create hierarchy link');
            }
            setRefreshKey(k => k + 1);
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    const handleDeleteHierarchy = (hierarchy) => {
        showAlertDialog({
            title: 'Remove Hierarchy',
            message: `Are you sure you want to remove the link between ${hierarchy.parent_type_name} and ${hierarchy.child_type_name}?`,
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/group-type-hierarchies/${hierarchy.id}/delete/`, {
                        method: 'DELETE'
                    });
                    if (!resp.ok) throw new Error("Failed to delete hierarchy");
                    setRefreshKey(k => k + 1);
                } catch (error) {
                    showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
                }
            }
        });
    };

    // State for hierarchy creation
    const [hParent, setHParent] = useState('');
    const [hChild, setHChild] = useState('');

    return (
        <div className="d-flex flex-column w-100" style={{ background: "#f8fafc", minHeight: '100vh', overflowX: 'hidden' }}>
            {/* Header */}
            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-light rounded-circle shadow-sm"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark">Group Structure</h4>
                        <small className="text-muted d-none d-md-block">Define organization levels (e.g., Division, Dept) and rules.</small>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => {
                            const isNested = window.location.pathname.includes('/groups/');
                            if (isNested) {
                                navigate(`/dashboard/applications/${applicationId}/groups`);
                            } else {
                                navigate(`/dashboard/applications/${applicationId}/group-types/groups`);
                            }
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.primary;
                            e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.color = theme.primary;
                        }}
                        style={{ background: '#fff', border: `1px solid ${theme.primary}`, color: theme.primary, borderRadius: theme.buttonRadius, transition: 'all 0.2s ease-in-out' }}
                    >
                        <i className="bi bi-people-fill"></i>
                        View Groups
                    </button>
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => { setSelectedType(null); setShowForm(true); }}
                        style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius }}
                    >
                        <i className="bi bi-plus-lg"></i>
                        <span className="d-none d-sm-inline">New Type</span>
                    </button>
                </div>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1400 }}>
                {loading ? <ProgressBarDialog open={true} text_message="Loading..." /> : (
                    <div className="row g-4">
                        {/* Group Types List */}
                        <div className="col-12 col-lg-6">
                            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
                                <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                    <div className="d-flex align-items-center gap-3 mb-1">
                                        <div className="bg-light rounded-3 text-primary d-flex align-items-center justify-content-center shadow-sm" style={{ width: 40, height: 40 }}>
                                            <i className="bi bi-layers-fill fs-5"></i>
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-0 text-dark">Group Types</h6>
                                            <p className="text-muted small mb-0">Define the structural units.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body px-3 px-md-4 pt-3">
                                    {groupTypes.length === 0 ? (
                                        <div className="text-center text-muted py-5 bg-light rounded border border-dashed">
                                            <i className="bi bi-inbox fs-2 mb-2 d-block opacity-50"></i>
                                            No group types defined.
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-2">
                                            {groupTypes.map(type => (
                                                <div key={type.id} className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between p-3 border rounded bg-white shadow-sm transition-all hover-shadow gap-2">
                                                    <span className="fw-bold text-dark d-flex align-items-center gap-2">
                                                        <i className="bi bi-dot fs-3 text-secondary d-sm-none"></i>
                                                        {type.name}
                                                    </span>
                                                    <div className="d-flex gap-2 align-self-end align-self-sm-auto">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary border-0"
                                                            onClick={() => { setSelectedType(type); setShowForm(true); }}
                                                            title="Edit"
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger border-0"
                                                            onClick={() => handleDeleteType(type)}
                                                            title="Delete"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Hierarchy Configuration */}
                        <div className="col-12 col-lg-6">
                            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
                                <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                    <div className="d-flex align-items-center gap-3 mb-1">
                                        <div className="bg-light rounded-3 text-info d-flex align-items-center justify-content-center shadow-sm" style={{ width: 40, height: 40 }}>
                                            <i className="bi bi-diagram-2-fill fs-5"></i>
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-0 text-dark">Hierarchy Rules</h6>
                                            <p className="text-muted small mb-0">Establish valid relationships.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body px-3 px-md-4 pt-3">
                                    {/* Add Hierarchy Form - Responsive Stack */}
                                    <div className="p-3 bg-light rounded mb-4 border">
                                        <div className="d-flex flex-column flex-md-row gap-2 align-items-md-end">
                                            <div className="flex-grow-1 w-100">
                                                <label className="small fw-bold text-muted mb-1 text-uppercase" style={{ fontSize: '0.7rem' }}>Parent Type</label>
                                                <select
                                                    className="form-select form-select-sm border-0 shadow-sm"
                                                    value={hParent}
                                                    onChange={e => setHParent(e.target.value)}
                                                    style={{ height: 38, cursor: 'pointer', borderRadius: theme.inputRadius }}
                                                >
                                                    <option value="">Select Parent...</option>
                                                    {groupTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                </select>
                                            </div>
                                            <div className="d-none d-md-block mb-2 text-muted"><i className="bi bi-arrow-right"></i></div>
                                            <div className="d-md-none text-center text-muted"><i className="bi bi-arrow-down"></i></div>
                                            <div className="flex-grow-1 w-100">
                                                <label className="small fw-bold text-muted mb-1 text-uppercase" style={{ fontSize: '0.7rem' }}>Child Type</label>
                                                <select
                                                    className="form-select form-select-sm border-0 shadow-sm"
                                                    value={hChild}
                                                    onChange={e => setHChild(e.target.value)}
                                                    style={{ height: 38, cursor: 'pointer', borderRadius: theme.inputRadius }}
                                                >
                                                    <option value="">Select Child...</option>
                                                    {groupTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                </select>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-sm rounded-pill shadow-sm px-4 d-flex align-items-center justify-content-center"
                                                disabled={!hParent || !hChild || hParent === hChild}
                                                onClick={() => {
                                                    const p = groupTypes.find(x => x.id === parseInt(hParent));
                                                    const c = groupTypes.find(x => x.id === parseInt(hChild));
                                                    handleAddHierarchy(p, c);
                                                    setHParent('');
                                                    setHChild('');
                                                }}
                                                style={{ height: 38, fontSize: '0.8rem', background: theme.primary, border: 'none' }}
                                            >
                                                <i className="bi bi-link-45deg me-2"></i>Link
                                            </button>
                                        </div>
                                    </div>

                                    {/* Hierarchy List */}
                                    <div className="d-flex flex-column gap-2">
                                        {hierarchies.map(h => (
                                            <div key={h.id} className="d-flex align-items-center justify-content-between p-3 border rounded bg-white shadow-sm transition-all hover-shadow">
                                                <div className="d-flex flex-wrap align-items-center gap-2">
                                                    <span className="badge bg-light text-dark border px-3 py-2">{h.parent_type_name}</span>
                                                    <i className="bi bi-arrow-right text-muted small mx-1"></i>
                                                    <span className="badge bg-light text-primary border border-primary px-3 py-2">{h.child_type_name}</span>
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-outline-danger border-0 rounded-circle d-flex align-items-center justify-content-center ms-2"
                                                    onClick={() => handleDeleteHierarchy(h)}
                                                    title="Remove Rule"
                                                    style={{ width: 32, height: 32 }}
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            </div>
                                        ))}
                                        {hierarchies.length === 0 && (
                                            <div className="text-muted small text-center py-4 fst-italic border rounded border-dashed bg-light">
                                                No rules defined. <br />Add a rule above to allow nesting.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showForm && (
                <OrganizationGroupTypeForm
                    applicationId={applicationId}
                    groupType={selectedType}
                    onClose={() => setShowForm(false)}
                    onSuccess={() => setRefreshKey(k => k + 1)}
                />
            )}
        </div>
    );
}
